import { batchService, studentService, STUDENT_TYPES } from '../services';

export const SECONDARY_TRADE_CODES = ['NIT', 'SOD', 'ETEL', 'CSA'];

export const parseStudentList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const getAssignedTrades = (context) => {
  const sources = [
    context?.instructor?.assignedTrades,
    context?.assignedTrades,
    context?.user?.assignedTrades,
  ];
  for (const src of sources) {
    if (Array.isArray(src) && src.length > 0) return src.map((t) => String(t).trim()).filter(Boolean);
  }
  return [];
};

export const getStudentTrade = (student) => {
  if (!student || typeof student !== 'object') return '';
  return String(
    student.fieldOfStudy ||
      student.trade ||
      student.program ||
      student.field_of_study ||
      ''
  ).trim();
};

/** Map free-text program names to NIT / SOD / ETEL / CSA. */
export const normalizeTradeCode = (value) => {
  const u = String(value || '').trim().toUpperCase();
  if (!u) return '';
  for (const code of SECONDARY_TRADE_CODES) {
    if (u === code || u.startsWith(`${code} `) || u.startsWith(`${code}-`) || u.includes(code)) {
      return code;
    }
  }
  if (u.includes('NETWORK') || u.includes(' NIT')) return 'NIT';
  if (u.includes('SOFTWARE') || u.includes(' SOD')) return 'SOD';
  if (u.includes('ELECTRON') || u.includes('TELECOM') || u.includes(' ETEL')) return 'ETEL';
  if (u.includes('COMPUTER SYSTEM') || u.includes(' CSA')) return 'CSA';
  return u;
};

export const getBatchIdFromStudent = (student) => {
  const b = student?.batch;
  if (!b) return null;
  return typeof b === 'object' ? b._id || b.id : b;
};

export const isSecondaryStudent = (student) => {
  const type = student?.studentType;
  if (!type) return true;
  const t = String(type).toLowerCase();
  return (
    type === STUDENT_TYPES.SECONDARY ||
    t === 'secondarystudent' ||
    t.includes('secondary')
  );
};

/**
 * Instructor sees students in their trade, or students in the cohort with no trade set yet.
 */
export const studentMatchesTrades = (student, assignedTrades) => {
  if (!Array.isArray(assignedTrades) || assignedTrades.length === 0) return true;
  const code = normalizeTradeCode(getStudentTrade(student));
  if (!code) return true;
  const assignedCodes = assignedTrades.map(normalizeTradeCode).filter(Boolean);
  return assignedCodes.includes(code);
};

export const mergeStudentsById = (...lists) => {
  const map = new Map();
  lists.flat().forEach((s) => {
    if (s?._id) map.set(String(s._id), s);
  });
  return [...map.values()];
};

/**
 * Students visible to an instructor: cohort members matching assigned trades.
 */
export const resolveInstructorStudents = async (context) => {
  const trades = getAssignedTrades(context);
  const batchId = context?.batch?._id || context?.batch;

  let students = parseStudentList(context?.students);

  if (batchId) {
    try {
      const fromBatch = await batchService.getBatchStudents(batchId);
      students = mergeStudentsById(students, parseStudentList(fromBatch));
    } catch {
      /* batch endpoint may be unavailable */
    }
  }

  if (batchId && students.length === 0) {
    try {
      const all = await studentService.getAllStudents();
      const pool = parseStudentList(all).filter(isSecondaryStudent);
      const inCohort = pool.filter(
        (s) => String(getBatchIdFromStudent(s)) === String(batchId)
      );
      if (inCohort.length > 0) {
        students = mergeStudentsById(students, inCohort);
      } else if (pool.length > 0) {
        // Cohort API empty and batch not linked on student records — use trade-matched secondary students
        students = trades.length > 0
          ? pool.filter((s) => studentMatchesTrades(s, trades))
          : pool;
      }
    } catch {
      /* ignore */
    }
  }

  students = students.filter(isSecondaryStudent);

  if (trades.length > 0) {
    students = students.filter((s) => studentMatchesTrades(s, trades));
  }

  return students;
};
